import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Phone, Lock, Eye, EyeOff } from 'lucide-react'
import { getOrgBySlug } from '../data/mock'
import { apiUrl } from '../config/api'
import { sispoliBridge } from '../lib/sispoliBridge'
import useTheme from '../hooks/useTheme'

interface OrgConfig {
  name: string
  logo_url: string | null
  primary_color: string
}

type View = 'login' | 'register' | 'verify'

export default function LoginPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const fallbackOrg = getOrgBySlug(slug || '')
  const [orgLoaded, setOrgLoaded] = useState(false)
  const [org, setOrg] = useState<OrgConfig>({
    name: fallbackOrg.appName,
    logo_url: null,
    primary_color: fallbackOrg.colorPrimary,
  })

  useEffect(() => {
    fetch(apiUrl(`/v1/community/${slug}/config`), { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.data) {
          setOrg({
            name: data.data.name,
            logo_url: data.data.logo_url,
            primary_color: data.data.primary_color || '#5865F2',
          })
        }
      })
      .catch(() => {})
      .finally(() => setOrgLoaded(true))
  }, [slug])

  const [view, setView] = useState<View>('login')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  // Login
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')

  // Register
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [regPassword, setRegPassword] = useState('')
  const [regConfirm, setRegConfirm] = useState('')

  // Verify
  const [code, setCode] = useState('')

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11)
    if (digits.length <= 2) return digits
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
  }

  const cleanPhone = (value: string) => value.replace(/\D/g, '')

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await fetch(apiUrl(`/v1/community/${slug}/auth/login`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ identifier: cleanPhone(identifier) || identifier, password }),
      })
      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Erro ao entrar')
        return
      }

      localStorage.setItem('token', data.token)
      localStorage.setItem('user', JSON.stringify(data.user))
      sispoliBridge.notifyLogin(data.token)
      navigate(`/${slug}/dashboard`)
    } catch {
      setError('Erro de conexão')
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (regPassword !== regConfirm) {
      setError('As senhas não coincidem')
      return
    }

    setLoading(true)

    try {
      const res = await fetch(apiUrl(`/v1/community/${slug}/auth/register`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name,
          phone: cleanPhone(phone),
          password: regPassword,
          password_confirmation: regConfirm,
        }),
      })
      const data = await res.json()

      if (!res.ok) {
        setError(data.error || data.message || 'Erro ao cadastrar')
        return
      }

      setView('verify')
    } catch {
      setError('Erro de conexão')
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await fetch(apiUrl(`/v1/community/${slug}/auth/verify`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name,
          phone: cleanPhone(phone),
          password: regPassword,
          code,
        }),
      })
      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Código inválido')
        return
      }

      localStorage.setItem('token', data.token)
      localStorage.setItem('user', JSON.stringify(data.user))
      sispoliBridge.notifyLogin(data.token)
      navigate(`/${slug}/dashboard`)
    } catch {
      setError('Erro de conexão')
    } finally {
      setLoading(false)
    }
  }

  if (!orgLoaded) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center px-6 py-10 bg-white">
        <div className="max-w-sm w-full flex flex-col items-center">
          <div className="w-20 h-20 rounded-2xl bg-gray-100 animate-pulse mb-4" />
          <div className="h-6 w-36 bg-gray-100 rounded-lg animate-pulse mb-2" />
          <div className="h-4 w-48 bg-gray-50 rounded-lg animate-pulse mb-10" />
          <div className="w-full space-y-4">
            <div>
              <div className="h-3 w-28 bg-gray-100 rounded animate-pulse mb-2" />
              <div className="h-12 w-full bg-gray-100 rounded-xl animate-pulse" />
            </div>
            <div>
              <div className="h-3 w-16 bg-gray-100 rounded animate-pulse mb-2" />
              <div className="h-12 w-full bg-gray-100 rounded-xl animate-pulse" />
            </div>
            <div className="h-12 w-full bg-gray-200 rounded-xl animate-pulse" />
            <div className="h-4 w-44 bg-gray-50 rounded animate-pulse mx-auto" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-dvh flex flex-col items-center justify-center px-6 py-10"
      style={{
        backgroundColor: theme === 'dark' ? '#111214' : '#F5F5F5',
        '--color-primary': org.primary_color,
        '--color-primary-hover': org.primary_color,
      } as React.CSSProperties}
    >
      {/* Logo da org */}
      {org.logo_url ? (
        <img
          src={org.logo_url}
          alt={org.name}
          className="w-20 h-20 rounded-2xl object-cover mb-4"
        />
      ) : (
        <div
          className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-bold mb-4"
          style={{ backgroundColor: org.primary_color || '#5865F2' }}
        >
          {org.name.charAt(0)}
        </div>
      )}
      <h1 className="text-xl font-bold text-content-normal mb-1">{org.name}</h1>
      <p className="text-sm text-content-muted mb-8">
        {view === 'login' ? 'Entre na sua comunidade' : view === 'register' ? 'Crie sua conta' : 'Verificação'}
      </p>

      {/* Card */}
      <div className="w-full max-w-sm">
        {/* Erro */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-status-danger/10 border border-status-danger/30 text-sm text-status-danger animate-fade-in">
            {error}
          </div>
        )}

        {/* LOGIN */}
        {view === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4 animate-fade-in">
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1.5">Telefone ou e-mail</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  type="text"
                  value={identifier}
                  onChange={e => setIdentifier(e.target.value)}
                  placeholder="(85) 99999-9999"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-surface-secondary border border-border-subtle text-sm text-content-normal placeholder:text-content-muted focus:outline-none focus:border-primary transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-content-muted mb-1.5">Senha</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Sua senha"
                  className="w-full pl-10 pr-10 py-3 rounded-xl bg-surface-secondary border border-border-subtle text-sm text-content-normal placeholder:text-content-muted focus:outline-none focus:border-primary transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50"
              style={{ backgroundColor: org.primary_color || '#5865F2' }}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>

            <p className="text-center text-sm text-content-muted">
              Não tem conta?{' '}
              <button type="button" onClick={() => { setView('register'); setError('') }} className="text-primary font-semibold">
                Cadastre-se
              </button>
            </p>
          </form>
        )}

        {/* REGISTER */}
        {view === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4 animate-fade-in">
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1.5">Nome completo</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Seu nome"
                className="w-full px-4 py-3 rounded-xl bg-surface-secondary border border-border-subtle text-sm text-content-normal placeholder:text-content-muted focus:outline-none focus:border-primary transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-content-muted mb-1.5">Telefone (WhatsApp)</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(formatPhone(e.target.value))}
                  placeholder="(85) 99999-9999"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-surface-secondary border border-border-subtle text-sm text-content-normal placeholder:text-content-muted focus:outline-none focus:border-primary transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-content-muted mb-1.5">Senha</label>
              <input
                type="password"
                value={regPassword}
                onChange={e => setRegPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="w-full px-4 py-3 rounded-xl bg-surface-secondary border border-border-subtle text-sm text-content-normal placeholder:text-content-muted focus:outline-none focus:border-primary transition-colors"
                required
                minLength={6}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-content-muted mb-1.5">Confirmar senha</label>
              <input
                type="password"
                value={regConfirm}
                onChange={e => setRegConfirm(e.target.value)}
                placeholder="Repita a senha"
                className="w-full px-4 py-3 rounded-xl bg-surface-secondary border border-border-subtle text-sm text-content-normal placeholder:text-content-muted focus:outline-none focus:border-primary transition-colors"
                required
                minLength={6}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50"
              style={{ backgroundColor: org.primary_color || '#5865F2' }}
            >
              {loading ? 'Enviando código...' : 'Cadastrar'}
            </button>

            <p className="text-center text-sm text-content-muted">
              Já tem conta?{' '}
              <button type="button" onClick={() => { setView('login'); setError('') }} className="text-primary font-semibold">
                Entrar
              </button>
            </p>
          </form>
        )}

        {/* VERIFY CODE */}
        {view === 'verify' && (
          <form onSubmit={handleVerify} className="space-y-4 animate-fade-in">
            <div className="text-center mb-2">
              <p className="text-sm text-content-muted">
                Enviamos um código de 6 dígitos para o WhatsApp
              </p>
              <p className="text-sm font-semibold text-content-normal mt-1">{phone}</p>
            </div>

            <div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="w-full px-4 py-4 rounded-xl bg-surface-secondary border border-border-subtle text-2xl text-center font-bold text-content-normal placeholder:text-content-muted tracking-[0.5em] focus:outline-none focus:border-primary transition-colors"
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full py-3 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50"
              style={{ backgroundColor: org.primary_color || '#5865F2' }}
            >
              {loading ? 'Verificando...' : 'Verificar e entrar'}
            </button>

            <p className="text-center text-sm text-content-muted">
              Não recebeu?{' '}
              <button type="button" className="text-primary font-semibold">
                Reenviar código
              </button>
            </p>

            <button
              type="button"
              onClick={() => { setView('register'); setError(''); setCode('') }}
              className="w-full text-center text-sm text-content-muted"
            >
              Voltar
            </button>
          </form>
        )}
      </div>

      {/* Powered by */}
      <p className="mt-10 text-xs text-content-muted opacity-50">
        Powered by SisPoli
      </p>
    </div>
  )
}
